import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScanLine, Coins, PackageOpen } from 'lucide-react';
import { apiUrl } from '../utils/apiUrl';
import './GiftShop.css';

function GiftShop() {
  const [points, setPoints] = useState(0);
  const [isPaying, setIsPaying] = useState(false);
  const [showShake, setShowShake] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [prize, setPrize] = useState(null);

  useEffect(() => {
    const savedPoints = localStorage.getItem('durian_points');
    if (savedPoints) {
      setPoints(parseInt(savedPoints, 10));
    }
  }, []);

  const handlePay = () => {
    setIsPaying(true);
    
    // Simulate payment
    setTimeout(async () => {
      try {
        const response = await fetch(apiUrl('/api/durian/pay'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: 120 })
        });
        const data = await response.json();
        
        if (data.status === 'Success') {
          completePayment(data.points);
        }
      } catch (error) {
        completePayment(50); // Fallback
      }
    }, 1500);
  };

  const completePayment = (earnedPoints) => {
    const newPoints = points + earnedPoints;
    setPoints(newPoints);
    localStorage.setItem('durian_points', newPoints.toString());
    setIsPaying(false);
    
    // Trigger shake to win
    setTimeout(() => {
      setShowShake(true);
    }, 500);
  };

  const handleDeviceShake = () => {
    if (isShaking) return;
    setIsShaking(true);
    
    // Simulate shake duration and prize drop
    setTimeout(() => {
      setPrize('Durian Keychain');
      setIsShaking(false);
    }, 2000);
  };

  const closeShakeModal = () => {
    setShowShake(false);
    setPrize(null);
  };

  return (
    <div className="page-container shop-page">
      <div className="header">
        <h2>The Gift Shop</h2>
        <p>Don’t leave empty-handed! Discover our exclusive orchard souvenirs.</p>
      </div>

      <div className="points-card card">
        <div className="points-display">
          <Coins size={32} color="var(--durian-gold)" />
          <div className="points-text">
            <span className="points-value">{points}</span>
            <span className="points-label">Durian Points</span>
          </div>
        </div>
      </div>

      <div className="payment-section card">
        <h3>Checkout</h3>
        <p>Ready to purchase your souvenirs? Scan the merchant code to pay with VIO Wallet.</p>
        
        <div className="mock-bill">
          <div className="bill-row">
            <span>Premium Durian Paste</span>
            <span>$80.00</span>
          </div>
          <div className="bill-row">
            <span>Durian Coffee (Box)</span>
            <span>$40.00</span>
          </div>
          <div className="bill-row total">
            <span>Total</span>
            <span>$120.00</span>
          </div>
        </div>

        <button 
          className="btn-primary pay-btn" 
          onClick={handlePay}
          disabled={isPaying}
        >
          <ScanLine size={20} />
          {isPaying ? 'Processing...' : 'Scan to Pay'}
        </button>
      </div>

      {/* Shake to Win Modal */}
      <AnimatePresence>
        {showShake && (
          <motion.div 
            className="shake-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="shake-content">
              {!prize ? (
                <>
                  <h2>Shake to Win!</h2>
                  <p>You earned a chance to win a prize for your purchase.</p>
                  
                  <motion.div 
                    className="shake-tree"
                    animate={isShaking ? { 
                      rotate: [-5, 5, -5, 5, -5, 5, 0],
                      transition: { duration: 0.5, repeat: 3 }
                    } : {}}
                  >
                    🌳
                  </motion.div>
                  
                  <p className="shake-instruction">
                    {isShaking ? 'Shaking...' : 'Shake your phone or tap the button!'}
                  </p>
                  
                  <button 
                    className="btn-primary" 
                    onClick={handleDeviceShake}
                    disabled={isShaking}
                  >
                    Shake Now
                  </button>
                </>
              ) : (
                <motion.div
                  initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
                  animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  transition={{ type: "spring", bounce: 0.5 }}
                  className="prize-reveal"
                >
                  <div className="prize-image-wrapper">
                    <div className="prize-glow"></div>
                    <img src="/durian-keychain.png" alt="Durian Keychain" className="prize-image" />
                  </div>
                  <h2>Congratulations!</h2>
                  <p>You won a</p>
                  <h3 className="prize-name">{prize}</h3>
                  <button className="btn-primary" onClick={closeShakeModal}>
                    Awesome!
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default GiftShop;
